<template>
  <Head>
    <title>{{ linkGroup.company?.name }}</title>
  </Head>
  <FontLinks :fonts="fontLinks" />
  <div
    v-if="linkGroup.qrCode"
    class="fixed right-4 bottom-4 hidden lg:block"
  >
    <SpectreCard size="base">
      <SpectreCardContent class="space-y-sm">
        <SpectreHeading
          as="h3"
          size="sm"
          class="text-center"
        >
          View on mobile
        </SpectreHeading>
        <img
          :src="`data:image/png;base64,${linkGroup.qrCode.base64}`"
          alt=""
          class="size-28"
        >
      </SpectreCardContent>
    </SpectreCard>
  </div>
  <div
    class="flex min-h-dvh flex-col lg:bg-inherit"
    :style="{ backgroundColor: content.backgroundColor }"
  >
    <div class="flex flex-1 lg:mt-24">
      <main class="mx-auto w-full max-w-md flex-1">
        <div class="lg:h-full lg:rounded-sm lg:rounded-b-none lg:shadow-sm lg:bg-white">
          <div class="space-y-6 p-6 lg:pb-24">
            <img
              v-if="content.logoUrl"
              :src="content.logoUrl"
              alt=""
              class="mx-auto mt-6 w-1/2"
              :style="{
                transform: `scale(${content.logoSize / 100})`,
              }"
            >
            <ProfilesSection
              v-if="content.iconPosition === 'top'"
              :profiles="content.profiles.filter((profile) => profile.display)"
              :icon-style="content.iconStyle"
              @click="trackProfileClick"
            />
            <SpectreStack>
              <section
                v-for="section in sections"
                :key="section.id"
              >
                <div v-if="section.type === 'property_slider'">
                  <Deferred data="properties">
                    <template #fallback>
                      <div class="flex aspect-[1.333/1] items-center justify-center">
                        <SpectreSpinner ariaLabel="Loading properties" />
                      </div>
                    </template>
                    <CarouselRoot
                      v-model:page="activeIndex"
                      :slide-count="properties?.length ?? 0"
                      spacing="12px"
                    >
                      <CarouselItemGroup>
                        <CarouselItem
                          v-for="(property, index) in properties"
                          :key="property.id"
                          :index="index"
                        >
                          <Component
                            :is="property.websiteListingLink ? 'a' : 'div'"
                            :href="property.websiteListingLink"
                            @click="property.websiteListingLink && trackPropertyClick(property)"
                          >
                            <PropertyCard :image="(property.media as MediaData[])[0].thumbnailUrl ?? ''">
                              <PropertyCardTitle
                                v-if="property.address"
                                as="h5"
                              >
                                {{ property.address.thoroughfare }} {{ property.address.outcode }}
                              </PropertyCardTitle>
                              <PropertyCardDescription v-if="property.salesListing">
                                <template v-if="property.salesListing.priceQualifier">
                                  {{ property.salesListing.priceQualifier }} £{{ property.salesListing.priceFormatted }}
                                </template>
                                <template v-else>
                                  £{{ property.salesListing.priceFormatted }}
                                </template>
                              </PropertyCardDescription>
                              <PropertyCardDescription v-else-if="property.lettingsListing">
                                £{{ property.lettingsListing.pricePcmFormatted }} PCM
                              </PropertyCardDescription>
                            </PropertyCard>
                          </Component>
                        </CarouselItem>
                      </CarouselItemGroup>
                      <CarouselIndicatorGroup class="mt-base flex justify-center gap-8">
                        <CarouselIndicator
                          v-for="(property, index) in properties"
                          :key="property.id"
                          :index="index"
                          :class="[
                            activeIndex !== index && 'bg-gray-500',
                            'relative size-3 rounded-full',
                          ]"
                          :style="{
                            backgroundColor: activeIndex === index ? brandSettings.colors.secondary : undefined,
                          }"
                        >
                          <SpectreTouchTarget />
                        </CarouselIndicator>
                      </CarouselIndicatorGroup>
                    </CarouselRoot>
                  </Deferred>
                </div>
                <SpectreButton
                  v-if="section.type === 'newsletter_sign_up'"
                  :leading-icon="asLinkData(section).icon ? iconComponents.get(asLinkData(section).icon!) : undefined"
                  block
                  size="xl"
                  :style="{
                    backgroundColor: brandSettings.colors.primary,
                    fontFamily: brandSettings.text.body.fontFamily,
                  }"
                  @click="trackButtonClick(section); toggleShowModal(true)"
                >
                  {{ asLinkData(section).buttonText }}
                </SpectreButton>
                <SpectreButton
                  v-if="section.type === 'whatsapp'"
                  as="a"
                  :href="`https://wa.me/${asLinkData(section).buttonUrl}`"
                  :leading-icon="asLinkData(section).icon ? iconComponents.get(asLinkData(section).icon!) : undefined"
                  block
                  size="xl"
                  :style="{
                    backgroundColor: brandSettings.colors.primary,
                    fontFamily: brandSettings.text.body.fontFamily,
                  }"
                  @click="trackButtonClick(section)"
                >
                  {{ asLinkData(section).buttonText }}
                </SpectreButton>
                <SpectreButton
                  v-if="section.type === 'custom' || section.type === 'website'"
                  as="a"
                  :href="formatUrl(asLinkData(section).buttonUrl)"
                  :leading-icon="asLinkData(section).icon ? iconComponents.get(asLinkData(section).icon!) : undefined"
                  block
                  size="xl"
                  :style="{
                    backgroundColor: brandSettings.colors.primary,
                    fontFamily: brandSettings.text.body.fontFamily,
                  }"
                  @click="trackButtonClick(section)"
                >
                  {{ asLinkData(section).buttonText }}
                </SpectreButton>
                <SpectreButton
                  v-if="section.type === 'book_valuation' || section.type === 'instant_valuation'"
                  as="a"
                  :href="asLinkData(section).buttonUrl ?? undefined"
                  :leading-icon="asLinkData(section).icon ? iconComponents.get(asLinkData(section).icon!) : undefined"
                  block
                  size="xl"
                  :style="{
                    backgroundColor: brandSettings.colors.primary,
                    fontFamily: brandSettings.text.body.fontFamily,
                  }"
                  @click="trackButtonClick(section)"
                >
                  {{ asLinkData(section).buttonText }}
                </SpectreButton>
              </section>
            </SpectreStack>
            <ProfilesSection
              v-if="content.iconPosition === 'bottom'"
              :profiles="content.profiles.filter((profile) => profile.display)"
              :icon-style="content.iconStyle"
              @click="trackProfileClick"
            />
          </div>
        </div>
      </main>
    </div>
  </div>
  <SpectreModal
    as="form"
    :open="showModal"
    @close="toggleShowModal(false)"
    @submit.prevent="onSubmit"
  >
    <SpectreModalContent class="relative">
      <div class="absolute top-2 right-2">
        <SpectreIconButton
          ariaLabel="Close modal"
          :icon="faXmark"
          color="tertiary"
          type="button"
          @click="toggleShowModal(false)"
        />
      </div>
      <SpectreStack>
        <h2
          class="text-lg font-semibold"
          :style="{
            fontFamily: brandSettings.text.heading.fontFamily,
            color: brandSettings.text.heading.textColor,
          }"
        >
          Receive our latest property news
        </h2>
        <p
          class="text-sm"
          :style="{
            fontFamily: brandSettings.text.body.fontFamily,
            color: brandSettings.text.body.textColor,
          }"
        >
          Sign up to receive updates on the local market, new instructions, and expert tips.
        </p>
        <SpectreField required>
          <SpectreLabel class="sr-only">
            Email address
          </SpectreLabel>
          <SpectreInput
            v-model="form.email"
            type="email"
            placeholder="Email address"
          />
        </SpectreField>
        <SpectreButton
          as="button"
          type="submit"
          block
          size="lg"
          :loading="form.processing"
          :style="{
            backgroundColor: brandSettings.colors.primary,
            fontFamily: brandSettings.text.body.fontFamily,
          }"
        >
          Subscribe
        </SpectreButton>
        <SpectreText>
          You can unsubscribe at any time by clicking the link in the footer of our emails.
        </SpectreText>
      </SpectreStack>
    </SpectreModalContent>
  </SpectreModal>
</template>

<script lang="ts" setup>
import ProfilesSection from '@/Pages/LinkGroups/Partials/ProfilesSection.vue';
import { computed, ref } from 'vue';
import {
  SpectreButton, SpectreCard, SpectreCardContent,
  SpectreField, SpectreHeading,
  SpectreIconButton,
  SpectreInput,
  SpectreLabel,
  SpectreModal,
  SpectreModalContent,
  SpectreText,
  SpectreSpinner,
  SpectreBlankLayout,
  SpectreTouchTarget, SpectreStack,
} from '@agentsoftware/spectre-ui';
import { sortBy } from 'lodash-es';
import { Deferred, Head, useForm } from '@inertiajs/vue3';
import { useToggle } from '@vueuse/core';

import { CarouselIndicator, CarouselIndicatorGroup, CarouselItem, CarouselItemGroup, CarouselRoot } from '@ark-ui/vue';
import useLinkGroupSectionIcons from '@/Composables/useLinkGroupSectionIcons';
import useLinkGroupTracking from '@/Composables/useLinkGroupTracking';
import { store as contactsStore } from '@/routes/link-groups/contacts';
import useInternalFonts from '@/Composables/useInternalFonts';
import PropertyCard from '@/Pages/LinkGroups/Partials/PropertyCard/PropertyCard.vue';
import PropertyCardTitle from '@/Pages/LinkGroups/Partials/PropertyCard/PropertyCardTitle.vue';
import PropertyCardDescription from '@/Pages/LinkGroups/Partials/PropertyCard/PropertyCardDescription.vue';
import FontLinks from '@/Components/FontLinks.vue';
import { brandFontLinks, type FontLink } from '@/utils/fontLinks';
import { faXmark } from '@awesome.me/kit-644ec404bf/icons/classic/regular';
import { type SectionData } from '@/types';
import LinkGroupContentData = App.Data.LinkGroup.LinkGroupContentData;
import LinkGroupData = App.Data.LinkGroupData;
import PropertyData = App.Data.Atlas.PropertyData;
import MediaData = App.Data.Atlas.MediaData;
import BrandData = App.Data.BrandData;
import BrandSettingsData = AgentSoftware.LaravelSocialiteSpectreId.Data.Settings.BrandSettingsData;
import LinkData = App.Data.LinkGroup.Section.LinkData;

defineOptions({
  layout: SpectreBlankLayout,
});

const props = defineProps<{
  content: LinkGroupContentData;
  linkGroup: LinkGroupData & {
    brand: BrandData;
  };
  properties?: PropertyData[];
}>();

const form = useForm<{
  email: string;
}>({
  email: '',
});

const activeIndex = ref<number>(0);

const [showModal, toggleShowModal] = useToggle<boolean>(false);

const onSubmit = () => {
  form.submit(contactsStore({ linkGroup: props.linkGroup.id }), {
    onSuccess: () => {
      toggleShowModal(false);
    },
  });
};

const internalFonts = useInternalFonts;

const sections = computed(() => sortBy(props.content.sections.filter((section: SectionData) => section.display), 'order'));

const brandSettings = computed(() => props.linkGroup.brand.settings as BrandSettingsData);

const internalFamilies = internalFonts.map((font) => font.family);

const fontLinks = computed<FontLink[]>(() =>
  brandFontLinks([{ settings: brandSettings.value }])
    .filter((link) => !internalFamilies.includes(link.family)),
);

const iconComponents = useLinkGroupSectionIcons;

const { track } = useLinkGroupTracking(props.linkGroup.id);

const trackButtonClick = (section: SectionData) => {
  track({
    type: 'button_click',
    section_type: section.type,
    section_id: section.id,
  });
};

const trackPropertyClick = (property: PropertyData) => {
  track({
    type: 'property_click',
    metadata: { property_id: property.id },
  });
};

const trackProfileClick = (profileType: string) => {
  track({
    type: 'button_click',
    metadata: { profile_type: profileType },
  });
};

const asLinkData = (section: SectionData): LinkData => section.content as LinkData;

const formatUrl = (url: string | null | undefined) => {
  if (!url) return '';
  return /^https:\/\//i.test(url) ? url : `https://${url}`;
};
</script>
